import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  Accordion,
  ButtonUI,
  Card,
  Checkbox,
  CustomThemeProvider,
  EmptyState,
  InputSearch,
  Loader,
  showNotification,
  Tabs,
  TabsItemProps,
  Tag,
  TaptapList,
  Toast,
  Tooltip,
  TooltipButton,
  TooltipInteractionEnum,
  useQueryFetch
} from 'sonata-react-components';

import { CancelPrompt } from '@/components/common/POIImporter/Prompts/CancelPrompt';
import { ConfirmPrompt } from '@/components/common/POIImporter/Prompts/ConfirmPrompt';
import { openGoogleMaps } from '@/helpers/common/poiAttribution.helper';
import { postAddVenues } from '@/services/venue.service';
import { usePOIImporter } from '../../../../POIImporter/POIImporter.context';
import { ATTRIBUTION_RADIUS_IN_METERS } from '../../../constants';
import { useMultiFlightMap } from '../../../MultiFlightMap.context';
import {
  findMatchingTemporaryVenue,
  restoreVenueAssociation,
  uniqueVenuesByGeoData
} from '../../../MultiFlightMap.helpers';
import { getAttributionPOIsInfo, getProcessErrorsCSVUrl } from '../../../multiFlightMap.resources';
import { AttributionRadiusPrompt } from '../AttributionRadiusPrompt/AttributionRadiusPrompt';
import { FileUploader } from '../FileUploader/FileUploader';
import { RowInfo } from '../RowInfo/RowInfo';

import type { Venue } from 'sonata-static-storage/lib/schemas/venue/venue.schema';

import './POIImporterResultList.scss';

import { filterVenuesBySearchTearm } from './poiSearch.helpers';

type Props = {
  enablePoiFinder: boolean;
  onGoBack: () => void;
  onAddComplete?: () => void;
  onCancelProcess?: () => void;
};

type State = {
  errorCsvIsLoading: boolean;
  foundSearchTerm: string;
  isActivationAreasEnabled: boolean;
  isAdding: boolean;
  notFoundSearchTerm: string;
  selectedIds: Set<string>;
  showActivationAreasPrompt: boolean;
  showCancelPrompt: boolean;
  showPrompt: boolean;
  showToast: boolean;
};

type ProximityListItem = {
  id: string;
  status: 'default';
  subtitle: string;
  title: string;
  venue: Partial<Venue>;
};

export function POIImporterResultList({ enablePoiFinder, onAddComplete, onCancelProcess, onGoBack }: Props) {
  const { formatMessage } = useIntl();
  const { attributionMode, file, isModalShowed, possibleWrongCountry, processId, setFile, setShowModal, venuesAdded } =
    usePOIImporter();

  const { addLocationCircle, entityId, entityType, onAddBatchVenues, temporaryVenues, updateVenue, user } =
    useMultiFlightMap();

  const isProximityPointsMode = attributionMode === 'proximityPoints';

  const venuesFound = enablePoiFinder
    ? uniqueVenuesByGeoData(venuesAdded.filter(venue => venue.geoDataId !== undefined && venue.geoDataId !== ''))
    : venuesAdded;
  const venuesNotFound = enablePoiFinder
    ? venuesAdded.filter(venue => venue.geoDataId === undefined || venue.geoDataId === '')
    : [];

  // POIs of the file that are already in the entity, counted once per POI of the file (not per venue of the entity)
  const alreadyAddedVenues = venuesFound.filter(venue => {
    const match = findMatchingTemporaryVenue(venue, temporaryVenues);
    return Boolean(match) && !match?.deleted;
  });

  const geoDataIds = Array.from(new Set(venuesFound.map(v => v.geoDataId).filter((id): id is string => Boolean(id))));

  const country = venuesFound[0]?.country || venuesAdded[0]?.country || 'ESP';
  const sourceId = venuesFound[0]?.geoDataSourceId || (country ? `poibrands${country.toLowerCase()}` : undefined);

  const { data: enrichedResponse, isLoading } = useQueryFetch(
    ['enrich', sourceId, geoDataIds.join(',')],
    async () => {
      if (!sourceId || geoDataIds.length === 0) {
        return Promise.resolve({ items: [] });
      }
      return getAttributionPOIsInfo(sourceId, country, geoDataIds);
    },
    {
      cacheTime: 0,
      enabled: geoDataIds.length > 0 && Boolean(sourceId) && isModalShowed && enablePoiFinder,
      staleTime: 0
    }
  );

  const enrichedMap = useMemo(() => {
    const items = (enrichedResponse && enrichedResponse.items) || [];
    const m = new Map<string, any>();
    items.forEach((item: any) => {
      if (item.geoDataId) {
        m.set(String(item.geoDataId), item);
      }
      if (item.id) {
        m.set(String(item.id), item);
      }
    });
    return m;
  }, [enrichedResponse]);

  const [
    {
      errorCsvIsLoading,
      foundSearchTerm,
      isActivationAreasEnabled,
      isAdding,
      notFoundSearchTerm,
      showActivationAreasPrompt,
      showCancelPrompt,
      showToast,
      selectedIds,
      showPrompt
    },
    setState
  ] = useState<State>({
    errorCsvIsLoading: false,
    foundSearchTerm: '',
    isActivationAreasEnabled: false,
    isAdding: false,
    notFoundSearchTerm: '',
    selectedIds: new Set(venuesFound.map(v => v._id).filter((id): id is string => Boolean(id))),
    showActivationAreasPrompt: false,
    showCancelPrompt: false,
    showPrompt: false,
    showToast: true
  });

  useEffect(() => {
    if (enablePoiFinder && isModalShowed && venuesNotFound.length > 0) {
      showNotification(
        'warning',
        formatMessage({
          defaultMessage:
            'We were unable to locate some of the POIs from your file. Download the CSV file with the missing entries, make corrections and re-upload it to try again.',
          id: 'poiImporter.modal.notification.notFound'
        })
      );
    }
  }, [enablePoiFinder, isModalShowed, processId]);

  const toggleSelect = (id: string) => {
    setState(prevState => {
      const next = new Set(prevState.selectedIds);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return { ...prevState, selectedIds: next };
    });
  };

  const handleDownloadErrorsCSV = async () => {
    if (!processId) {
      return;
    }
    setState(prevState => ({ ...prevState, errorCsvIsLoading: true }));
    const { url } = await getProcessErrorsCSVUrl(processId);
    setState(prevState => ({ ...prevState, errorCsvIsLoading: false }));
    window.open(url, '_blank');
  };

  function VenuesNotFound() {
    const filteredVenuesNotFound = filterVenuesBySearchTearm(venuesNotFound, notFoundSearchTerm);

    return (
      <div className="poiImporterResultList__tabContentInner">
        {showToast && (
          <Toast
            autoClose={false}
            className="poiImporterResultList__disclaimer"
            closeButton={false}
            icon="fal fa-exclamation-triangle"
            secondaryText={
              possibleWrongCountry
                ? formatMessage({
                    defaultMessage:
                      'Double-check that you selected the correct country for this file and that your geospatial information is mapped properly.',
                    id: 'poiImporter.resultList.wrongCountry.secondary'
                  })
                : formatMessage({
                    defaultMessage:
                      "Some entries couldn't be recognized. Please review the common errors below or ensure your file is formatted correctly.",
                    id: 'poiImporter.resultList.error.secondary'
                  })
            }
            text={
              possibleWrongCountry
                ? formatMessage({
                    defaultMessage: "We couldn't find any matches.",
                    id: 'poiImporter.resultList.wrongCountry.title'
                  })
                : formatMessage({
                    defaultMessage: 'Confirm your POI upload.',
                    id: 'poiImporter.resultList.error'
                  })
            }
            type="danger"
          />
        )}
        <div className="poiImporterResultList__scrollArea">
          <div className="poiImporterResultList__accordionsGroup">
            <Accordion>
              <Accordion.Header isBold>
                {formatMessage({
                  defaultMessage: 'Quick guide to find your POIs',
                  id: 'poiImporter.resultList.quickGuideTitle'
                })}
                <Accordion.Icon />
              </Accordion.Header>
              <Accordion.Content className="poiImporterResultList__accordionContent">
                <div className="poiImporterResultList__guideContainer">
                  <p className="poiImporterResultList__guideIntro">
                    Some addresses don’t match any known area. To resolve this:
                  </p>
                  <br />
                  <ol className="poiImporterResultList__guideList">
                    <li>
                      <span className="poiImporterResultList__semiBold">
                        Download the file with the missing areas clicking the button below.
                      </span>
                      It includes only the entries that need to be reviewed.
                    </li>
                    <li>
                      <span className="poiImporterResultList__semiBold">Verify each address.</span> You can use the
                      preview icon next to each item to go to the areas.
                    </li>
                    <li>
                      <span className="poiImporterResultList__semiBold">Correct the name or address in the file.</span>{' '}
                      Update only the information that is inaccurate or incomplete.
                    </li>
                    <li>
                      <span className="poiImporterResultList__semiBold">Save the file and upload it again.</span> The
                      platform will re-check all items and add the corrected ones.
                    </li>
                  </ol>
                  <br />
                  <p className="poiImporterResultList__guideNote">
                    <span className="poiImporterResultList__semiBold">Note:</span> You don’t need to reload the original
                    full file, only the corrected one.
                  </p>
                </div>
              </Accordion.Content>
            </Accordion>
            <Accordion>
              <Accordion.Header isBold>
                {formatMessage({
                  defaultMessage: 'Frequent problems',
                  id: 'poiImporter.resultList.frequentProblemsTitle'
                })}
                <Accordion.Icon />
              </Accordion.Header>
              <Accordion.Content className="poiImporterResultList__accordionContent">
                <div className="poiImporterResultList__guideContainer">
                  <p className="poiImporterResultList__guideIntro">
                    Check the following requirements to fix the issue:
                  </p>
                  <br />
                  <ol className="poiImporterResultList__guideList">
                    <li>
                      <span className="poiImporterResultList__semiBold">File format:</span>
                      Ensure your file is in .CSV format.
                    </li>
                    <li>
                      <span className="poiImporterResultList__semiBold">File size:</span>
                      The maximun limit is 1GB.
                    </li>
                  </ol>
                  <br />
                  <p className="poiImporterResultList__guideNote">
                    Still having trouble? Visit our{' '}
                    <a className="poiImporterResultList__link" href="/faq" rel="noopener noreferrer" target="_blank">
                      FAQs page <i className="fa-solid fa-arrow-up-right-from-square"></i>
                    </a>{' '}
                    or{' '}
                    <a className="poiImporterResultList__link" href="mailto:product.support@taptapnetworks.com">
                      Contact us
                    </a>
                    .
                  </p>
                </div>
              </Accordion.Content>
            </Accordion>
          </div>
          <InputSearch
            className="poiImporterResultList__notFoundSearch"
            id="poiImporterResultList_notFoundSearch"
            lens
            name="notFoundSearch"
            onChange={val => setState(prevState => ({ ...prevState, notFoundSearchTerm: val }))}
            placeholder={formatMessage({
              defaultMessage: 'Search by name, address...',
              id: 'poiImporter.resultList.notFound.search.placeholder'
            })}
            tiny
            value={notFoundSearchTerm}
          />
          <div className="poiImporterResultList__rowContainer">
            {filteredVenuesNotFound.length === 0 && notFoundSearchTerm ? (
              <EmptyState
                description={formatMessage({
                  defaultMessage: 'No results match your search. Try a different name, address or coordinates.',
                  id: 'poiImporter.resultList.search.emptyState.description'
                })}
                icon="fal fa-magnifying-glass"
                id="poiImporterResultList_notFoundEmptyState"
                show
                title={formatMessage({
                  defaultMessage: 'No results found',
                  id: 'poiImporter.resultList.search.emptyState.title'
                })}
              />
            ) : (
              filteredVenuesNotFound.map(venue => (
                <RowInfo
                  description={
                    venue.address !== 'N/A'
                      ? venue.address
                      : venue.latlng
                        ? `${venue.latlng?.lat}, ${venue.latlng?.lng}` || 'N/A'
                        : 'N/A'
                  }
                  id={venue._id!}
                  key={venue._id!}
                  onClick={() => openGoogleMaps(venue)}
                  title={venue.name || 'N/A'}
                />
              ))
            )}
          </div>
        </div>
        <div className="poiImporterResultList__downloadBtnContainer">
          <CustomThemeProvider themeContrast="darkAI">
            <ButtonUI
              className="poiImporterResultList__downloadBtn"
              icon="fa-regular fa-arrow-down-to-line"
              loading={errorCsvIsLoading}
              onClick={handleDownloadErrorsCSV}
              shape="fullRounded"
              size="medium"
              text={formatMessage({
                defaultMessage: 'Download CSV with issues',
                id: 'poiImporter.button.downloadCSV'
              })}
              type="tertiary"
            />
          </CustomThemeProvider>
        </div>
      </div>
    );
  }

  function VenuesFound() {
    if (isLoading) {
      return (
        <div className="poiImporterResultList__venuesFoundSpinner">
          <Loader id={'poiImporterResultList-spinner'} type="landing" variant="default" />
        </div>
      );
    }
    return (
      <div className="poiImporterResultList__tabContentInner">
        <div className="poiImporterResultList__rowContainer">
          {venuesFound.map(venue => {
            const enriched = enrichedMap.get(String(venue.geoDataId)) || {};
            return (
              <RowInfo
                description={enriched.address || venue.address || 'N/A'}
                id={venue._id!}
                key={venue._id!}
                onClick={() => openGoogleMaps(venue)}
                subtitle={enriched.brand}
                title={enriched.name || venue.name || 'N/A'}
              />
            );
          })}
        </div>
      </div>
    );
  }

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    const newFile = event.currentTarget.files?.[0];
    if (!newFile) {
      return;
    }
    setFile(newFile, true);
  };

  const confirmCancelPOIs = () => {
    onCancelProcess?.();
  };

  const handleCancelPOIs = () => {
    setState(prevState => ({
      ...prevState,
      showCancelPrompt: true
    }));
  };

  const confirmAddPOIs = async (radiusInKm?: number) => {
    const selectedFoundVenues = venuesFound.filter(v => selectedIds.has(v._id!));

    const venuesToRestore: Partial<Venue>[] = [];
    const venuesToCreate: typeof selectedFoundVenues = [];
    selectedFoundVenues.forEach(v => {
      const match = findMatchingTemporaryVenue(v, temporaryVenues);
      if (!match) {
        venuesToCreate.push(v);
      } else if (match.deleted) {
        venuesToRestore.push(restoreVenueAssociation(match, entityId, entityType));
      }
      // else: already associated with this entity — skip, it's a duplicate.
    });

    setState(prevState => ({ ...prevState, isAdding: true }));
    try {
      if (venuesToRestore.length > 0) {
        updateVenue(venuesToRestore);
      }

      if (venuesToCreate.length > 0) {
        const normalized = venuesToCreate.map(v => ({
          address: v.address,
          country: v.country,
          deleted: false,
          geoDataId: v.geoDataId,
          geoDataSourceId: v.geoDataSourceId,
          groups: v.groups,
          latlng: v.latlng,
          mooh: v.mooh,
          name: v.name,
          sourceType: 'custom',
          type: v.type,
          user: user?._id || (user as any)?.id
        }));

        const persisted = await postAddVenues(normalized as any, {} as any);
        await onAddBatchVenues(persisted as any, {
          attribution: true,
          attributionMode: attributionMode === 'proximityPoints' ? 'footfall' : 'driveToStore',
          ...(isProximityPointsMode && radiusInKm && { attributionRadiusInMeters: radiusInKm * 1000 })
        });

        if (isActivationAreasEnabled && radiusInKm) {
          await Promise.all(
            persisted
              .filter(venue => venue.latlng?.lat && venue.latlng?.lng)
              .map(venue => addLocationCircle(venue.latlng.lat, venue.latlng.lng, radiusInKm))
          );
        }
      }

      setShowModal(false);
      if (onAddComplete) {
        onAddComplete();
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      setState(prevState => ({ ...prevState, isAdding: false }));
      showNotification(
        'error',
        formatMessage({ defaultMessage: "Couldn't add POIs, try again", id: 'poiImporter.notification.add.error' })
      );
    }
  };

  const handleAddPOIs = async (radiusInMeters?: number) => {
    if ((isActivationAreasEnabled || isProximityPointsMode) && !showActivationAreasPrompt) {
      setState(prevState => ({
        ...prevState,
        showActivationAreasPrompt: true
      }));
    } else if (venuesNotFound.length > 0 && !isActivationAreasEnabled) {
      setState(prevState => ({
        ...prevState,
        showPrompt: true
      }));
    } else {
      await confirmAddPOIs(radiusInMeters ? radiusInMeters / 1000 : undefined);
    }
  };

  const tabs: TabsItemProps[] = [
    {
      badge: venuesNotFound.length,
      className: 'poiImporterResultList__notFoundTab',
      component: VenuesNotFound(),
      id: 'notFound',
      tabDisabled: venuesNotFound.length === 0,
      title: formatMessage({
        defaultMessage: 'Not found',
        id: 'poiImporter.modal.tabs.notFound'
      })
    },
    {
      badge: venuesFound.length,
      className: 'poiImporterResultList__foundTab',
      component: <VenuesFound />,
      id: 'found',
      title: formatMessage({
        defaultMessage: 'Found',
        id: 'poiImporter.modal.tabs.found'
      })
    }
  ];

  const filteredVenuesFound = filterVenuesBySearchTearm(venuesFound, foundSearchTerm, venue => {
    const enriched = enrichedMap.get(String(venue.geoDataId)) || {};
    return `${enriched.name || ''}${enriched.address || ''}`;
  });

  const proximityListData: ProximityListItem[] = filteredVenuesFound.map(venue => {
    const enriched = enrichedMap.get(String(venue.geoDataId)) || {};
    return {
      id: venue._id!,
      status: 'default',
      subtitle: enriched.address || venue.address || 'N/A',
      title: enriched.name || venue.name || 'N/A',
      venue
    };
  });

  const defaultSelectedProximityItems = proximityListData.filter(item => selectedIds.has(item.id));

  const handleProximitySelectionChange = (items: ProximityListItem[]) => {
    setState(prevState => ({ ...prevState, selectedIds: new Set(items.map(item => item.id)) }));
  };

  if (!file) {
    return null;
  }

  return (
    <main className="poiImporterResultList">
      <div className="poiImporterResultList__sectionsContainer">
        <section className="poiImporterResultList__foundContainer">
          <header>
            <div className="poiImporterResultList__file">
              <div className="poiImporterResultList__fileButton">
                <ButtonUI
                  icon="fa-light fa-arrow-left"
                  id={`poiImporter_fileBack`}
                  onClick={onGoBack}
                  shape="link"
                  size="ultraSmall"
                  type="primary"
                />
              </div>
              <FileUploader file={file} />
            </div>
          </header>
          <article className="poiImporterResultList__content">
            {isProximityPointsMode ? (
              <TaptapList
                className="poiImporterResultList__coordinatesListWrapper"
                data={proximityListData}
                defaultSelectedItems={defaultSelectedProximityItems}
                id="poiImporterResultList-coordinates"
                onSelectionChange={items => handleProximitySelectionChange(items as ProximityListItem[])}
                selectionType="rowClick"
              >
                <div className="poiImporterResultList__coordinatesToolbarRow">
                  <TaptapList.Toolbar.SelectionSummary />
                  <div className="poiImporterResultList__coordinatesRightGroup">
                    <TaptapList.Toolbar.SelectAll />
                    <InputSearch
                      className="poiImporterResultList__coordinatesSearch"
                      id="poiImporterResultList_foundSearch"
                      lens
                      name="foundSearch"
                      onChange={val => setState(prevState => ({ ...prevState, foundSearchTerm: val }))}
                      placeholder={formatMessage({
                        defaultMessage: 'Search by name, address...',
                        id: 'poiImporter.resultList.found.search.placeholder'
                      })}
                      tiny
                      value={foundSearchTerm}
                    />
                  </div>
                </div>
                <TaptapList.RowsList<ProximityListItem>>
                  {listItem =>
                    listItem.map(item => (
                      <TaptapList.RowsList.Row id={item.id} key={item.id}>
                        <div className="poiImporterResultList__coordinateRowContent">
                          <span className="poiImporterResultList__coordinateRowTitle">{item.title}</span>
                          <span className="poiImporterResultList__coordinateRowSubtitle">{item.subtitle}</span>
                        </div>
                        <ButtonUI
                          className="poiImporterResultList__coordinateRowAction"
                          icon="fa-duotone fa-regular fa-map-location-dot"
                          onClick={(event: React.MouseEvent) => {
                            event.stopPropagation();
                            openGoogleMaps(item.venue);
                          }}
                          shape="link"
                          size="small"
                          type="secondary"
                        />
                      </TaptapList.RowsList.Row>
                    ))
                  }
                </TaptapList.RowsList>
              </TaptapList>
            ) : !enablePoiFinder ? (
              <VenuesFound />
            ) : (
              <Tabs
                className="poiImporterResultList__tabs"
                hideNoneActivePanels={false}
                initialTab={venuesNotFound.length > 0 ? 'notFound' : 'found'}
                shape="default"
                tabs={tabs}
                variant="filled"
              />
            )}
          </article>
        </section>
        {!isProximityPointsMode && (
          <section className="poiImporterResultList__preview">
            <div className="poiImporterResultList__previewHeader">
              <div className="poiImporterResultList__previewHeaderLeft">
                <h2 className="poiImporterResultList__previewTitle">{selectedIds.size}</h2>
                <p className="poiImporterResultList__previewSubtitle">
                  {formatMessage({
                    defaultMessage: 'POIs',
                    id: 'poiImporter.resultList.preview.title'
                  })}
                </p>
              </div>
              <div className="poiImporterResultList__previewHeaderRight">
                {alreadyAddedVenues.length > 0 && (
                  <Tag id={`tag-POI-alreadyAdded`} status="information">
                    {formatMessage(
                      {
                        defaultMessage: `{alreadyAddedCount} POI already added in {entity}`,
                        id: 'poiImporter.resultList.duplicates'
                      },
                      {
                        alreadyAddedCount: alreadyAddedVenues.length,
                        entity: entityType === 'lineItem' ? 'line item' : 'insertion order'
                      }
                    )}
                  </Tag>
                )}
                <Checkbox
                  active={selectedIds.size === venuesFound.length}
                  id={`poiImporter_selectAll`}
                  labelText={
                    selectedIds.size === venuesFound.length
                      ? formatMessage({ defaultMessage: 'Unselect all', id: 'poiImporter.resultList.unselectAll' })
                      : formatMessage({ defaultMessage: 'Select all', id: 'poiImporter.resultList.selectAll' })
                  }
                  onClickAction={() => {
                    if (selectedIds.size === venuesFound.length) {
                      setState(prevState => ({ ...prevState, selectedIds: new Set() }));
                    } else {
                      setState(prevState => ({
                        ...prevState,
                        selectedIds: new Set(venuesFound.map(v => v._id).filter((id): id is string => Boolean(id)))
                      }));
                    }
                  }}
                />
              </div>
            </div>
            <InputSearch
              className="poiImporterResultList__previewSearch"
              id="poiImporterResultList_foundSearch"
              lens
              name="foundSearch"
              onChange={val => setState(prevState => ({ ...prevState, foundSearchTerm: val }))}
              placeholder={formatMessage({
                defaultMessage: 'Search by name, address...',
                id: 'poiImporter.resultList.found.search.placeholder'
              })}
              tiny
              value={foundSearchTerm}
            />
            {isLoading ? (
              <Loader
                className="poiImporterResultList__previewSpinner"
                id={'poiImporterResultList-spinner'}
                type="landing"
                variant="default"
              />
            ) : venuesFound.length === 0 && possibleWrongCountry ? (
              <EmptyState
                description={formatMessage({
                  defaultMessage:
                    "Please try again, ensuring that your country selection and column mapping match your file's data.",
                  id: 'poiImporter.resultList.wrongCountry.emptyState.description'
                })}
                icon="fal fa-empty-set"
                id="poiImporterResultList_wrongCountryEmptyState"
                show
                title={formatMessage({
                  defaultMessage: 'No POIs could be recognized',
                  id: 'poiImporter.resultList.wrongCountry.emptyState.title'
                })}
              />
            ) : filteredVenuesFound.length === 0 && foundSearchTerm ? (
              <EmptyState
                description={formatMessage({
                  defaultMessage: 'No results match your search. Try a different name, address or coordinates.',
                  id: 'poiImporter.resultList.search.emptyState.description'
                })}
                icon="fal fa-magnifying-glass"
                id="poiImporterResultList_foundEmptyState"
                show
                title={formatMessage({
                  defaultMessage: 'No results found',
                  id: 'poiImporter.resultList.search.emptyState.title'
                })}
              />
            ) : (
              <div className="poiImporterResultList__previewContent">
                {filteredVenuesFound.map(venue => {
                  const enriched = enrichedMap.get(String(venue.geoDataId)) || {};
                  const bucket = sourceId && sourceId.includes('brand') ? 'brand-logos' : 'screens-logos';
                  const rawLogo = enriched.logo_s3;
                  const logo = rawLogo ? `${taptap.config.publicUrlBase}/${bucket}/${rawLogo}.svg` : undefined;

                  return (
                    <Card
                      className="poiImporterResultList__card"
                      isSelected={selectedIds.has(venue._id!)}
                      key={venue._id}
                      onClick={() => toggleSelect(venue._id!)}
                    >
                      {enriched.brand && (
                        <Card.Header align="right">
                          <Card.Label title={enriched.brand} variant="category">
                            {enriched.brand}
                          </Card.Label>
                        </Card.Header>
                      )}
                      {enriched.logo_s3 && <Card.Media alt={enriched.brand} src={logo}></Card.Media>}
                      <Card.Body align="left">
                        <Card.Label title={enriched.name || venue.name} variant="title">
                          {enriched.name || venue.name}
                        </Card.Label>
                        <Card.Label title={enriched.address || venue.address || 'N/A'} variant="subtitle">
                          {enriched.address || venue.address || 'N/A'}
                        </Card.Label>
                      </Card.Body>
                    </Card>
                  );
                })}
              </div>
            )}
          </section>
        )}
      </div>
      <footer className="poiImporterResultList__actions">
        {venuesNotFound.length > 0 && (
          <label className="poiImporterResultList__uploadButton" htmlFor={`multiple-import-csv-result`}>
            <ButtonUI
              icon="fa-solid fa-arrow-up-to-line"
              id={`poiImporter_uploadAgain`}
              size="medium"
              text={formatMessage({
                defaultMessage: 'Upload again',
                id: 'poiImporter.button.uploadAgain'
              })}
              type="secondary"
            />
            <input
              accept=".csv"
              className="poiImporterResultList__fileInput"
              hidden
              id={`multiple-import-csv-result`}
              name="multiple-import-csv-result"
              onChange={handleFileUpload}
              type="file"
            />
          </label>
        )}
        <div className="poiImporterResultList__actionsLeft">
          <ButtonUI
            id="poiImporter_cancel"
            onClick={handleCancelPOIs}
            shape="squareRounded"
            text={formatMessage({
              defaultMessage: 'Cancel',
              id: 'poiImporter.button.cancel'
            })}
            type="secondary"
          />
        </div>
        <div className="poiImporterResultList__actionsRight">
          <Tooltip
            config={{
              arrow: true,
              interactions: [TooltipInteractionEnum.hover],
              placement: 'top',
              strategy: 'fixed',
              text: formatMessage({
                defaultMessage: 'Continue only with selected POIs and save them as Activation Areas as well.',
                id: 'poiImporter.tooltip.activationAreas'
              }),
              type: 'primary'
            }}
          >
            <Checkbox
              disabled={selectedIds.size === 0 || isAdding}
              id={`poiImporter_activationAreas`}
              labelText={formatMessage({
                defaultMessage: 'Add & set as Activation Areas',
                id: `poiImporter.checkbox.activationAreas`
              })}
              onClickAction={() =>
                setState(prevState => ({ ...prevState, isActivationAreasEnabled: !prevState.isActivationAreasEnabled }))
              }
            />
          </Tooltip>
          <TooltipButton
            buttonConfig={{
              buttonDisabled: selectedIds.size === 0,
              buttonLoading: isAdding,
              buttonOnClick: handleAddPOIs,
              buttonShape: 'squareRounded',
              buttonSize: 'medium',
              buttonText: isProximityPointsMode
                ? formatMessage({
                    defaultMessage: 'Add Coordinates',
                    id: 'poiImporter.button.addPoints'
                  })
                : formatMessage({
                    defaultMessage: 'Add POIs',
                    id: 'poiImporter.button.addPOIs'
                  }),
              buttonType: 'primary'
            }}
            className="poiImporterResultList__addButtonTooltip"
            icon={isProximityPointsMode ? 'fa-solid fa-thumbtack' : 'fa-kit-duotone fa-upload-poi'}
            id={'poiImporterResultList-tooltip-button'}
            isButton={true}
            tooltipConfig={{
              tooltipEvent: 'mouseenter',
              tooltipPlace: 'top'
            }}
          >
            {formatMessage({
              defaultMessage: 'Continue only with selected zones',
              id: 'poiImporterResultList.tooltip.action.copy'
            })}
          </TooltipButton>
        </div>
      </footer>
      {showCancelPrompt && (
        <CancelPrompt
          onClose={() => setState(prevState => ({ ...prevState, showCancelPrompt: false }))}
          onConfirm={() => {
            confirmCancelPOIs();
            setState(prevState => ({ ...prevState, showCancelPrompt: false }));
          }}
        />
      )}
      {showPrompt && (
        <ConfirmPrompt
          onClose={() => setState(prevState => ({ ...prevState, showPrompt: false }))}
          onConfirm={() => {
            confirmAddPOIs();
            setState(prevState => ({ ...prevState, showPrompt: false }));
          }}
          selectedIdsSize={selectedIds.size}
          venuesAddedCount={venuesAdded.length}
          venuesNotFoundCount={venuesNotFound.length}
        />
      )}
      {showActivationAreasPrompt && (
        <AttributionRadiusPrompt
          defaultRadiusInMeters={isProximityPointsMode ? ATTRIBUTION_RADIUS_IN_METERS : undefined}
          onCancel={() => {
            setState(prevState => ({
              ...prevState,
              showActivationAreasPrompt: false
            }));
          }}
          onConfirm={radius => {
            setState(prevState => ({
              ...prevState,
              showActivationAreasPrompt: false
            }));
            handleAddPOIs(radius);
          }}
          title={
            isProximityPointsMode
              ? formatMessage({
                  defaultMessage: 'Confirm the radius for your Proximity Coordinates',
                  id: 'poiImporter.prompt.proximityRadius.title'
                })
              : undefined
          }
        />
      )}
    </main>
  );
}
